/**
 * @file ProductChannelsSection.tsx
 * @description Блок «Каналы продаж» в редакторе товара (phase17.1, шаг 8).
 *
 * Оверлей с наследованием (C1-C6): плитки card-каналов (витрины/маркетплейсы)
 * с тумблером публикации, счётчиком отличий и статусом синка. Клик по плитке →
 * drawer «Карточка канала». Заменяет прежний ProductChannelsSection (visibility).
 */

import { Loader2, Radio } from "lucide-react";
import { useCallback, useEffect, useState } from "react";

import { Alert } from "../../../../components/ui/alert";
import { useNotify } from "../../../../components/ui/NotifyProvider";
import { ApiError } from "../../../../lib/api-client";
import { channelListings, type ChannelSummary } from "../../../../lib/api-channel-listings";
import { ChannelCardDrawer } from "./ChannelCardDrawer";
import { ChannelTile } from "./ChannelTile";
import { SectionCard } from "./SectionCard";

interface Props {
  productId: string;
}

export function ProductChannelsSection({ productId }: Props) {
  const notify = useNotify();
  const [items, setItems] = useState<ChannelSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [openChannel, setOpenChannel] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setItems(await channelListings.list(productId));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Не удалось загрузить каналы");
    } finally {
      setLoading(false);
    }
  }, [productId]);

  useEffect(() => {
    void load();
  }, [load]);

  async function onTogglePublish(channel: string, next: boolean): Promise<void> {
    setBusy(channel);
    try {
      await channelListings.togglePublish(productId, channel, next);
      setItems((prev) =>
        prev.map((it) => (it.channel === channel ? { ...it, isPublished: next, hasListing: true } : it)),
      );
    } catch (err) {
      notify.error(err instanceof ApiError ? err.message : "Не удалось обновить публикацию");
    } finally {
      setBusy(null);
    }
  }

  const storefronts = items.filter((i) => i.channelType === "STOREFRONT");
  const marketplaces = items.filter((i) => i.channelType === "MARKETPLACE");

  return (
    // Сворачивается, как остальные вторичные секции карточки (замечание
    // пользователя 2026-09-26); состояние не запоминается — как у них.
    <SectionCard icon={<Radio />} title="Каналы продаж" collapsible>
      <p className="text-sm text-on-surface-variant mb-4">
        Где и как показывается товар. Юр-обязательная информация едина для всех каналов.
      </p>

      {loading ? (
        <div className="flex justify-center py-6">
          <Loader2 className="size-5 animate-spin text-on-surface-variant" />
        </div>
      ) : error ? (
        <Alert severity="error">{error}</Alert>
      ) : (
        <div className="flex flex-col gap-5">
          <ChannelGroup
            title="Витрины"
            items={storefronts}
            busy={busy}
            onToggle={onTogglePublish}
            onOpen={setOpenChannel}
          />
          <ChannelGroup
            title="Маркетплейсы"
            items={marketplaces}
            busy={busy}
            onToggle={onTogglePublish}
            onOpen={setOpenChannel}
          />
        </div>
      )}

      {openChannel ? (
        <ChannelCardDrawer
          productId={productId}
          channel={openChannel}
          onClose={() => setOpenChannel(null)}
          onSaved={() => void load()}
        />
      ) : null}
    </SectionCard>
  );
}

function ChannelGroup({
  title,
  items,
  busy,
  onToggle,
  onOpen,
}: {
  title: string;
  items: ChannelSummary[];
  busy: string | null;
  onToggle: (channel: string, next: boolean) => void;
  onOpen: (channel: string) => void;
}) {
  if (items.length === 0) return null;
  return (
    <div>
      <h4 className="type-caption font-medium text-on-surface-variant mb-2">
        {title}
      </h4>
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3">
        {items.map((item) => (
          <ChannelTile
            key={item.channel}
            item={item}
            busy={busy === item.channel}
            onToggle={(next) => onToggle(item.channel, next)}
            onOpen={() => onOpen(item.channel)}
          />
        ))}
      </div>
    </div>
  );
}
